import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import api from "@/lib/api";
import { toast } from "sonner";

const BENEFITS = [
  "Early access to new features",
  "Invitations to beta test upcoming tools",
  "Opportunities to vote on future development priorities",
  "Product updates from the founder",
  "Exclusive feedback opportunities",
];

const POSTSIGNUP_BULLETS = [
  "Early feature previews",
  "Testing invitations",
  "Development updates",
  "Community surveys",
];

export default function Community() {
  const [roles, setRoles] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", role: "", wish: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    api.get("/community/roles").then(({ data }) => setRoles(data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.role) {
      toast.error("Please complete the required fields.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/community/join", form);
      setSuccess(true);
      toast.success("Welcome, Founding ThoughtSmith™.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not register your membership.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-forge-900 text-forge-100 grain">
      <Header />
      <main className="max-w-3xl mx-auto px-6 lg:px-10 py-16 sm:py-24">
        <div className="text-[10px] tracking-[0.4em] uppercase text-gold-500 mb-4">The Founding ThoughtSmith™ Community</div>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mb-4 text-balance" data-testid="community-title">
          Become a Founding ThoughtSmith™.
        </h1>
        <p className="font-reading text-lg text-forge-100/75 leading-relaxed mb-10 max-w-2xl">
          Help shape the future of AI-powered critical thinking, civil discourse, intellectual literacy,
          and lifelong learning.
        </p>

        {success ? (
          <div className="border border-ember-500/40 bg-forge-800/60 p-8 sm:p-10 rounded-sm" data-testid="community-success">
            <div className="text-[10px] tracking-[0.4em] uppercase text-ember-500 mb-3">Membership Confirmed</div>
            <h2 className="font-serif text-3xl text-forge-100 mb-5">Thank you for joining the Founding ThoughtSmith™ Community.</h2>
            <p className="text-sm text-forge-100/65 mb-6">You will receive:</p>
            <ul className="space-y-3 mb-8">
              {POSTSIGNUP_BULLETS.map((b) => (
                <li key={b} className="flex items-start gap-3 text-forge-100/85">
                  <span className="text-ember-500 mt-1">✓</span>
                  <span className="font-reading text-base">{b}</span>
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap gap-3">
              <Link to="/" className="px-5 py-2.5 border border-white/15 hover:border-ember-500 hover:text-ember-400 text-sm tracking-wide rounded-sm transition-all">
                ← Back to ThoughtSmith™
              </Link>
              <Link to="/learn" className="px-5 py-2.5 bg-ember-500 hover:bg-ember-400 text-forge-900 font-semibold tracking-wide rounded-sm transition-all">
                Continue Reading
              </Link>
            </div>
          </div>
        ) : (
          <>
            <div className="mb-12 border-l-2 border-gold-500 pl-6">
              <div className="text-[10px] tracking-[0.3em] uppercase text-gold-500 mb-3">Members Receive</div>
              <ul className="space-y-2">
                {BENEFITS.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-forge-100/80">
                    <span className="text-ember-500 mt-1.5 text-xs">◆</span>
                    <span className="font-reading text-base sm:text-lg">{b}</span>
                  </li>
                ))}
              </ul>
            </div>

            <form onSubmit={submit} className="space-y-6" data-testid="community-form">
              <div className="text-[10px] uppercase tracking-[0.3em] text-ember-500 mb-2">Required</div>
              <input
                type="text"
                placeholder="Your name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-4 py-3 bg-forge-800 border border-white/10 rounded-sm text-forge-100 placeholder-forge-100/30 focus:outline-none focus:border-ember-500 transition-colors"
                required
                data-testid="community-name"
              />
              <input
                type="email"
                placeholder="Email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full px-4 py-3 bg-forge-800 border border-white/10 rounded-sm text-forge-100 placeholder-forge-100/30 focus:outline-none focus:border-ember-500 transition-colors"
                required
                data-testid="community-email"
              />
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-forge-100/50 mb-3">I am a…</div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {roles.map((r) => (
                    <button
                      type="button"
                      key={r}
                      onClick={() => setForm({ ...form, role: r })}
                      className={`px-3 py-2 text-sm border rounded-sm transition-all ${form.role === r ? "border-ember-500 bg-forge-800 text-ember-400" : "border-white/10 hover:border-ember-500/60 text-forge-100/80"}`}
                      data-testid={`community-role-${r.replace(/\s+/g, '-').toLowerCase()}`}
                    >
                      {r}
                    </button>
                  ))}
                </div>
              </div>

              <div className="pt-2">
                <div className="text-[10px] uppercase tracking-[0.3em] text-gold-500 mb-2">Optional</div>
                <label className="block font-reading text-base text-forge-100/85 mb-3">
                  What is one thing you wish schools taught better?
                </label>
                <textarea
                  rows={4}
                  value={form.wish}
                  onChange={(e) => setForm({ ...form, wish: e.target.value })}
                  placeholder="A sentence or two is enough."
                  className="w-full bg-transparent border-b border-white/15 focus:border-gold-500 outline-none py-3 text-forge-100 placeholder-forge-100/30 font-reading text-base leading-relaxed transition-colors resize-none"
                  data-testid="community-wish"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full px-5 py-3 bg-ember-500 hover:bg-ember-400 disabled:opacity-60 text-forge-900 font-semibold tracking-wide rounded-sm transition-all"
                data-testid="community-submit"
              >
                {loading ? "Adding you…" : "Become a Founding ThoughtSmith™"}
              </button>
            </form>
          </>
        )}
      </main>
    </div>
  );
}
